import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck } from "lucide-react";
import { shopApi, productLabel } from "@/lib/shopApi";
import { money, formatApiErrorDetail } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { fieldCls } from "@/components/store/AuthShell";
import { SummaryRow } from "@/pages/store/Cart";

export default function Checkout() {
  const { member, cart, cartReady, clearCart } = useMember();
  const placed = useRef(false);
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [d, setD] = useState({ name: member.delivery?.name || member.name, phone: member.delivery?.phone || "", address: member.delivery?.address || "" });
  const [codeInput, setCodeInput] = useState("");
  const [code, setCode] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const items = cart.map((i) => ({ product_id: i.product_id, qty: i.qty }));

  useEffect(() => { shopApi.get("/shop/store-info").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  const refresh = useCallback(async (c) => {
    try { setQuote((await shopApi.post("/shop/quote", { items, discount_code: c })).data); return true; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
  }, [JSON.stringify(items)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (items.length) refresh(code); }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cartReady) return <div className="py-20 text-center text-slate-500">Loading…</div>;
  if (!cart.length && !placed.current) return <Navigate to="/cart" replace />;

  const applyCode = async () => { const c = codeInput.trim().toUpperCase(); if (await refresh(c)) { setCode(c); if (c) toast.success("Discount code applied"); } };
  const place = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await shopApi.post("/shop/orders", { items, discount_code: code, delivery: d, save_delivery: true });
      placed.current = true;
      nav(`/account/orders/${data.id}?new=1`, { replace: true });
      clearCart();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={place} data-testid="checkout-page">
      <h1 className="font-head text-4xl font-extrabold tracking-tight">Checkout</h1>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
            <h2 className="font-head text-lg font-bold">Delivery</h2>
            <div className="mt-4 flex items-start gap-3 rounded-xl border border-sky-400 bg-sky-50 p-4" data-testid="checkout-shipping-info">
              <Truck className="mt-0.5 h-5 w-5 text-sky-700" />
              <div><div className="font-semibold text-slate-900">Flat-rate shipping</div><div className="text-sm text-slate-500">{money(info?.shipping_cost)} added to every order</div></div>
            </div>
          </section>
          <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
            <h2 className="font-head text-lg font-bold">Your details</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Full name</Label><Input required minLength={2} value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} className={fieldCls} data-testid="checkout-name-input" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input required minLength={6} value={d.phone} onChange={(e) => setD({ ...d, phone: e.target.value })} className={fieldCls} data-testid="checkout-phone-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={member.email} disabled className={fieldCls} data-testid="checkout-email-input" /></div>
            <div className="space-y-1.5"><Label>Delivery address</Label><Textarea required minLength={5} rows={3} value={d.address} onChange={(e) => setD({ ...d, address: e.target.value })} className="rounded-lg border-slate-200" placeholder="Street, suburb, state, postcode" data-testid="checkout-address-input" /></div>
            <p className="text-xs text-slate-500">These details are saved to your account for next time.</p>
          </section>
          <section className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
            <h2 className="font-head text-lg font-bold">Discount code</h2>
            <div className="mt-4 flex gap-2">
              <Input value={codeInput} onChange={(e) => setCodeInput(e.target.value)} placeholder="Enter code" className={`${fieldCls} uppercase`} data-testid="checkout-discount-input" />
              <button type="button" onClick={applyCode} className="h-11 shrink-0 rounded-lg border border-slate-900 px-5 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-900 hover:text-white" data-testid="checkout-discount-apply">Apply</button>
            </div>
          </section>
        </div>
        <aside className="h-fit space-y-3 rounded-xl border border-slate-200 bg-white p-5 lg:sticky lg:top-24" data-testid="checkout-summary">
          <h2 className="font-head text-lg font-bold">Order summary</h2>
          {cart.map((i) => <div key={i.product_id} className="flex justify-between gap-3 text-sm"><span className="text-slate-700">{productLabel(i)} × {i.qty}</span><span className="font-medium">{money(i.price * i.qty)}</span></div>)}
          <div className="space-y-2 border-t border-slate-100 pt-3">
            <SummaryRow label="Subtotal" value={money(quote?.subtotal)} testid="checkout-subtotal" />
            {quote?.discount_amount - (quote?.shipping_discount || 0) > 0.001 && <SummaryRow label={`Discount (${quote.discount_code})`} value={`-${money(quote.discount_amount - (quote.shipping_discount || 0))}`} testid="checkout-discount" accent />}
            {quote?.auto_discount_amount > 0 && <SummaryRow label={`Bank transfer discount (${quote.auto_discount_percent}%)`} value={`-${money(quote.auto_discount_amount)}`} testid="checkout-auto-discount" accent />}
            <SummaryRow label="Shipping (flat rate)" value={money(quote?.shipping_cost)} testid="checkout-shipping" />
            {quote?.shipping_discount > 0 && <SummaryRow label={`Shipping discount (${quote.discount_code})`} value={`-${money(quote.shipping_discount)}`} testid="checkout-shipping-discount" accent />}
            <SummaryRow label="Total (AUD)" value={money(quote?.total)} strong testid="checkout-total" />
          </div>
          <div className="rounded-lg bg-sky-50 p-3 text-xs leading-relaxed text-slate-600">Payment is by bank transfer. You'll see our bank details and your payment reference after placing the order. Use only your order number as the payment description — <strong className="font-extrabold text-rose-700" data-testid="checkout-no-product-names">NO PRODUCT NAMES</strong>.</div>
          <button type="submit" disabled={busy || !quote} className="h-11 w-full rounded-lg bg-[#89CFF0] font-semibold text-slate-900 transition-colors hover:bg-[#74C2EC] disabled:opacity-60" data-testid="checkout-place-order">{busy ? "Placing order…" : "Place order"}</button>
        </aside>
      </div>
    </form>
  );
}
